// =============================================================================
//  LOT D — gestes de facturation partagés (profil « renovation » : Mario Bat).
// =============================================================================
//  · AnnulationFacture  : « Annuler la facture » = créer un AVOIR via la route existante
//                         `POST /{id}/avoir`, MOTIF OBLIGATOIRE. La facture émise n'est jamais
//                         supprimée ni modifiée.
//  · AvoirsEtSolde      : les avoirs liés et le reste dû, tels que le SERVEUR les rend.
//  · FormEchelonnement  : acompte / intermédiaire / solde — le serveur décide de ce qui est permis.
//  Aucune règle légale rejouée ici : l'écran affiche, le serveur tranche.
// =============================================================================
import { useState } from "react";

const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 16,
};
const champ = {
  height: 36, borderRadius: 8, padding: "0 12px", fontSize: 13,
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", width: "100%",
};
const btnBase = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px",
  borderRadius: 8, fontSize: 13, fontWeight: 500, minHeight: 36,
};
const btnPrimaire = {
  ...btnBase, border: "none", background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)",
};
const btnSecondaire = {
  ...btnBase, background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)",
};
const btnDiscret = {
  ...btnBase, background: "transparent", border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)",
};
const nombre = { fontVariantNumeric: "tabular-nums" };

export function euros(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export const LIBELLE_TYPE = {
  acompte: "Facture d'acompte", intermediaire: "Facture intermédiaire", solde: "Facture de solde",
};

/** Bouton « Annuler la facture » + boîte de dialogue au motif obligatoire. */
export function AnnulationFacture({ facture, occupe, onAgir }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState("");
  const valide = motif.trim().length > 0;
  const confirmer = async () => {
    if (!valide) return;
    const fait = await onAgir(`/${facture.id}/avoir`, { motif: motif.trim() });
    if (fait) { setOuvert(false); setMotif(""); }
  };
  return (
    <>
      <button type="button" style={btnSecondaire} data-testid="simple-annuler" disabled={occupe}
              onClick={() => setOuvert(true)}>
        Annuler la facture
      </button>
      {ouvert && (
        <div role="dialog" aria-modal="true" aria-labelledby="annulation-titre" data-testid="annulation-dialogue"
             style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center",
                      justifyContent: "center", padding: 16, background: "var(--oh-bg)" }}>
          <div style={{ ...carte, width: "100%", maxWidth: 480, display: "grid", gap: 12 }}>
            <h3 id="annulation-titre" style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--oh-text)" }}>
              Annuler la facture {facture.numero || ""}
            </h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--oh-text)" }}>
              Une facture émise ne se supprime pas. L'annulation crée un avoir du montant total ; la facture
              d'origine reste consultable.
            </p>
            <div>
              <label style={etiquette} htmlFor="annulation-motif">Motif de l'annulation (obligatoire)</label>
              <textarea id="annulation-motif" data-testid="annulation-motif" value={motif} rows={3}
                        placeholder="Pourquoi annuler cette facture ?" disabled={occupe}
                        onChange={(e) => setMotif(e.target.value)}
                        style={{ ...champ, height: "auto", padding: 12, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" style={btnDiscret} data-testid="annulation-retour" disabled={occupe}
                      onClick={() => { setOuvert(false); setMotif(""); }}>
                Retour
              </button>
              <button type="button" style={btnPrimaire} data-testid="annulation-confirmer"
                      disabled={occupe || !valide} onClick={confirmer}>
                Créer l'avoir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Avoirs liés à la facture et reste dû — valeurs du serveur, rien n'est recalculé ici. */
export function AvoirsEtSolde({ facture }) {
  const avoirs = facture.avoirs || [];
  const solde = facture.solde;
  const credite = solde && Number(solde.montant_credite) > 0;
  if (!avoirs.length && !credite) return null;
  return (
    <div style={carte} data-testid="simple-avoirs">
      <p style={{ ...etiquette, marginBottom: 8 }}>Avoirs et reste dû</p>
      {avoirs.map((a) => (
        <p key={a.id} style={{ margin: "4px 0", fontSize: 13, color: "var(--oh-text)", ...nombre }}
           data-testid={`simple-avoir-${a.id}`}>
          Avoir <strong>{a.numero || "brouillon"}</strong> · {euros(a.total_ttc)}
        </p>
      ))}
      {credite && (
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--oh-text)", ...nombre }}>
          Facturé {euros(solde.total_ttc)} · crédité {euros(solde.montant_credite)} ⇒{" "}
          <strong data-testid="simple-solde-restant">reste dû {euros(solde.solde_ttc)}</strong>
        </p>
      )}
    </div>
  );
}

/** Acompte / intermédiaire / solde. `types` = ceux que l'écran propose ; le serveur peut refuser, on DIT le motif. */
export function FormEchelonnement({ types, pourcentageRestant, occupe, onCreer, testidPrefix = "echelon" }) {
  const [type, setType] = useState(types[0]);
  const [pourcentage, setPourcentage] = useState("");
  const avecPct = type !== "solde";
  const pct = Number(pourcentage);
  const valide = !avecPct || (Number.isFinite(pct) && pct > 0 && pct <= 100);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
         data-testid={`${testidPrefix}-formulaire`}>
      <div style={{ flex: "0 0 200px" }}>
        <label style={etiquette} htmlFor={`${testidPrefix}-type`}>Type de facture</label>
        <select id={`${testidPrefix}-type`} data-testid={`${testidPrefix}-type`} value={type}
                style={champ} disabled={occupe} onChange={(e) => setType(e.target.value)}>
          {types.map((t) => <option key={t} value={t}>{LIBELLE_TYPE[t]}</option>)}
        </select>
      </div>
      {avecPct && (
        <div style={{ flex: "0 0 140px" }}>
          <label style={etiquette} htmlFor={`${testidPrefix}-pct`}>
            Part du devis (%){pourcentageRestant != null ? ` — reste ${pourcentageRestant}` : ""}
          </label>
          <input id={`${testidPrefix}-pct`} data-testid={`${testidPrefix}-pct`} type="number" min="0" max="100"
                 step="any" style={champ} value={pourcentage} disabled={occupe}
                 onChange={(e) => setPourcentage(e.target.value)} />
        </div>
      )}
      <button type="button" style={btnSecondaire} data-testid={`${testidPrefix}-creer`}
              disabled={occupe || !valide}
              onClick={() => onCreer(type, avecPct ? pct : undefined)}>
        Créer la {LIBELLE_TYPE[type].toLowerCase()}
      </button>
    </div>
  );
}
